import { getMessaging, getToken, onMessage, isSupported, Messaging } from 'firebase/messaging';
import { supabase } from '../../lib/supabase';
import { app, auth } from '../../firebase';

/**
 * Push notification (Firebase Cloud Messaging) untuk PWA.
 *
 * Alur:
 *  1. Pegawai menekan "Aktifkan Notifikasi" (WAJIB lewat ketukan pengguna —
 *     iPhone menolak permintaan izin tanpa ketukan, Chrome menyembunyikannya).
 *  2. Service worker khusus FCM (`/firebase-messaging-sw.js`, dibuat saat build
 *     oleh generate-config.js) didaftarkan di scope terpisah dari service
 *     worker PWA, lalu token perangkat disimpan ke tabel Supabase `fcm_tokens`.
 *  3. Setiap aplikasi dibuka & izin sudah diberikan → token disinkronkan ulang
 *     tanpa bertanya (token bisa berganti).
 *  4. Aplikasi tertutup / di latar belakang → notifikasi ditampilkan oleh
 *     firebase-messaging-sw.js. Aplikasi sedang dibuka → ditampilkan oleh
 *     `startForegroundNotifications` (FCM tidak menampilkannya otomatis).
 *  5. Notifikasi diketuk → aplikasi dibuka / difokuskan ke halaman tujuan.
 */

const FCM_SW_URL = '/firebase-messaging-sw.js';
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';
const NOTIF_ICON = '/pwa-192.png';

let messagingPromise: Promise<Messaging | null> | null = null;

/** Messaging hanya bila browser mendukung (aman dipanggil berkali-kali). */
const getMessagingSafe = (): Promise<Messaging | null> => {
    if (!messagingPromise) {
        messagingPromise = isSupported()
            .then(ok => (ok ? getMessaging(app) : null))
            .catch(() => null);
    }
    return messagingPromise;
};

export const isPushSupported = (): boolean =>
    typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'Notification' in window
    && 'PushManager' in window;

export const getNotificationPermission = (): NotificationPermission | 'unsupported' =>
    isPushSupported() ? Notification.permission : 'unsupported';

export const isIOSDevice = (): boolean =>
    typeof navigator !== 'undefined'
    && (/iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

export const isStandalonePWA = (): boolean =>
    typeof window !== 'undefined'
    && (window.matchMedia?.('(display-mode: standalone)').matches || (window.navigator as any).standalone === true);

const SW_ACTIVATE_TIMEOUT_MS = 15000;

/**
 * Tunggu sampai service worker benar-benar aktif. Tepat setelah didaftarkan
 * (pertama kali / setelah data situs dihapus) worker masih "installing", dan
 * getToken gagal dengan "Subscription failed - no active Service Worker".
 */
const waitUntilActive = (registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> => {
    if (registration.active) return Promise.resolve(registration);
    const worker = registration.installing || registration.waiting;
    if (!worker) return Promise.reject(new Error('Service worker notifikasi tidak terpasang.'));
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            worker.removeEventListener('statechange', onChange);
            reject(new Error('Service worker notifikasi tidak aktif (cek /firebase-messaging-sw.js).'));
        }, SW_ACTIVATE_TIMEOUT_MS);
        const onChange = () => {
            if (worker.state === 'activated') {
                clearTimeout(timer);
                worker.removeEventListener('statechange', onChange);
                resolve(registration);
            } else if (worker.state === 'redundant') {
                clearTimeout(timer);
                worker.removeEventListener('statechange', onChange);
                reject(new Error('Service worker notifikasi gagal dipasang (cek /firebase-messaging-sw.js).'));
            }
        };
        worker.addEventListener('statechange', onChange);
        onChange();
    });
};

/** Registrasi service worker FCM yang sudah aktif (didaftarkan bila belum ada). */
let swUpdateChecked = false;

const getFcmRegistration = async (): Promise<ServiceWorkerRegistration> => {
    const existing = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE);
    // Scope FCM tidak pernah dinavigasi, jadi browser jarang memeriksa versi
    // baru firebase-messaging-sw.js. Periksa sekali setiap aplikasi dibuka.
    if (existing && !swUpdateChecked) {
        swUpdateChecked = true;
        existing.update().catch(() => undefined);
    }
    const registration = existing && (existing.active || existing.installing || existing.waiting)
        ? existing
        : await navigator.serviceWorker.register(FCM_SW_URL, { scope: FCM_SW_SCOPE });
    return waitUntilActive(registration);
};

/**
 * Ambil token FCM & simpan ke Supabase. TIDAK menampilkan dialog izin —
 * hanya berjalan bila izin sudah diberikan. Dipanggil setiap aplikasi dibuka.
 */
export const syncFcmToken = async (): Promise<string | null> => (await registerFcmToken()).token;

/**
 * Sama dengan syncFcmToken, tetapi mengembalikan alasan kegagalan
 * (dipakai halaman Cek Notifikasi).
 */
let registerInFlight: Promise<{ token: string | null; error: string | null }> | null = null;

export const registerFcmToken = (): Promise<{ token: string | null; error: string | null }> => {
    // Dipanggil bersamaan saat login (PushNotificationManager) dan oleh halaman
    // Cek Notifikasi: satu pendaftaran saja supaya tidak lahir dua token.
    if (!registerInFlight) {
        registerInFlight = registerFcmTokenOnce().finally(() => { registerInFlight = null; });
    }
    return registerInFlight;
};

const registerFcmTokenOnce = async (): Promise<{ token: string | null; error: string | null }> => {
    if (getNotificationPermission() !== 'granted') {
        return { token: null, error: 'Izin notifikasi belum diberikan.' };
    }

    const messaging = await getMessagingSafe();
    if (!messaging) return { token: null, error: 'Browser ini tidak mendukung Firebase Messaging.' };

    // @ts-ignore — disuntikkan Vite dari environment Vercel
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
        console.error('[FCM] VITE_FIREBASE_VAPID_KEY belum diatur di environment.');
        return { token: null, error: 'VITE_FIREBASE_VAPID_KEY belum diatur di Vercel.' };
    }

    try {
        const registration = await getFcmRegistration();
        const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
        if (!token) return { token: null, error: 'Firebase tidak mengembalikan token.' };
        const saveError = await saveTokenToSupabase(token);
        return { token, error: saveError };
    } catch (error) {
        console.error('[FCM] Gagal mengambil token:', error);
        const message = (error as Error)?.message || String(error);
        // Brave (tanpa "Google services for push messaging") & HP tanpa Google Play Services
        // gagal mendaftar ke layanan push dengan pesan ini.
        const hint = /push service error/i.test(message)
            ? ' Browser/perangkat ini tidak bisa terhubung ke layanan push Google. Di Brave: aktifkan "Use Google services for push messaging" di brave://settings/privacy. Atau gunakan Chrome.'
            : '';
        return { token: null, error: `Gagal mengambil token: ${message}.${hint}` };
    }
};

/**
 * Tampilkan notifikasi langsung dari perangkat ini, tanpa server. Bila ini pun
 * tidak muncul, penyebabnya pengaturan notifikasi HP/komputer, bukan pengiriman.
 */
export const showLocalTestNotification = async (): Promise<string | null> => {
    try {
        const registration = await getFcmRegistration();
        await registration.showNotification('Tes tampilan notifikasi', {
            body: 'Kalau ini muncul, perangkat ini bisa menampilkan notifikasi.',
            icon: NOTIF_ICON,
            badge: NOTIF_ICON,
            tag: `lokal-${Date.now()}`,
            data: { url: '/' },
        });
        return null;
    } catch (err) {
        return (err as Error)?.message || String(err);
    }
};

const UNREGISTER_TIMEOUT_MS = 5000;

/**
 * Dipanggil saat LOGOUT: perangkat ini berhenti menerima notifikasi akun tadi.
 * Hanya baris token di Supabase yang dihapus; langganan push di browser
 * dibiarkan, karena mencabut lalu membuat ulang langganan membuat push tidak
 * tampil di beberapa perangkat. Login berikutnya menyimpan token yang sama
 * atas nama akun yang login. Tidak pernah
 * melempar error dan paling lama UNREGISTER_TIMEOUT_MS supaya logout tidak macet.
 */
export const unregisterFcmToken = async (): Promise<void> => {
    if (getNotificationPermission() !== 'granted') return;
    const work = (async () => {
        const messaging = await getMessagingSafe();
        if (!messaging) return;
        const registration = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE);
        if (!registration?.active) return;
        // @ts-ignore — disuntikkan Vite dari environment Vercel
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
        const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
        if (token) {
            const { error } = await supabase.from('fcm_tokens').delete().eq('token', token);
            if (error) console.warn('[FCM] Gagal menghapus token saat logout:', error);
        }
    })().catch(err => console.warn('[FCM] Gagal menghapus token saat logout:', err));
    await Promise.race([work, new Promise(resolve => setTimeout(resolve, UNREGISTER_TIMEOUT_MS))]);
};

/**
 * Minta izin notifikasi (WAJIB dipanggil dari ketukan pengguna) lalu daftarkan token.
 */
export const requestNotificationPermission = async (): Promise<{
    status: NotificationPermission | 'unsupported';
    token: string | null;
}> => {
    if (!isPushSupported() || !(await getMessagingSafe())) {
        return { status: 'unsupported', token: null };
    }
    const status = await Notification.requestPermission();
    const token = status === 'granted' ? await syncFcmToken() : null;
    return { status, token };
};

/**
 * Simpan token ke Supabase. Upsert berdasarkan token: bila HP yang sama
 * dipakai akun lain, pemilik token diperbarui (notifikasi tidak nyasar).
 */
const saveTokenToSupabase = async (token: string): Promise<string | null> => {
    const user = auth.currentUser;
    if (!user || !user.email) return 'Belum login.';

    const email = user.email.toLowerCase().trim();
    // Token FCM berbeda per domain (portal, kesantrian, vercel.app, preview),
    // walau di browser yang sama → domain ikut jadi bagian identitas perangkat.
    const deviceInfo = `${window.location.host} | ${navigator.userAgent}`;
    const { error } = await supabase
        .from('fcm_tokens')
        .upsert({
            user_id: user.uid,
            email,
            token,
            device_info: deviceInfo,
            updated_at: new Date().toISOString(),
        }, { onConflict: 'token' });

    if (!error) {
        // Satu browser per domain = satu token aktif. Token lama dari browser &
        // domain yang sama (berganti saat data situs dihapus / service worker
        // dipasang ulang) dihapus supaya tidak menumpuk ratusan baris per akun.
        // Baris format lama (device_info = userAgent saja, tanpa domain) ikut
        // dihapus: domain lain akan mendaftar ulang saat dibuka.
        const { error: cleanupError } = await supabase
            .from('fcm_tokens')
            .delete()
            .eq('email', email)
            .in('device_info', [deviceInfo, navigator.userAgent])
            .neq('token', token);
        if (cleanupError) console.warn('[FCM] Gagal membersihkan token lama:', cleanupError);
    }

    if (error) {
        console.error('[FCM] Gagal menyimpan token ke Supabase:', error);
        return `Gagal menyimpan token ke Supabase: ${error.message}`;
    }
    return null;
};

/**
 * Tampilkan notifikasi saat aplikasi SEDANG DIBUKA (foreground).
 * FCM tidak menampilkan notifikasi sendiri ketika halaman sedang aktif.
 * Mengembalikan fungsi untuk berhenti mendengarkan.
 */
export const startForegroundNotifications = async (): Promise<() => void> => {
    const messaging = await getMessagingSafe();
    if (!messaging) return () => undefined;

    return onMessage(messaging, async (payload) => {
        const data = (payload.data || {}) as Record<string, string>;
        const title = data.title || payload.notification?.title;
        if (!title) return;
        const body = data.body || payload.notification?.body || '';
        try {
            const registration = await getFcmRegistration();
            await registration.showNotification(title, {
                body,
                icon: NOTIF_ICON,
                badge: NOTIF_ICON,
                tag: data.tag || undefined,
                data: { url: data.url || '/' },
            });
        } catch (err) {
            console.warn('[FCM] Gagal menampilkan notifikasi foreground:', err);
        }
    });
};

/**
 * Pindah halaman saat notifikasi diketuk ketika aplikasi sudah terbuka
 * (service worker FCM mengirim pesan `FCM_NAVIGATE`).
 */
export const listenNotificationNavigation = (): (() => void) => {
    if (!('serviceWorker' in navigator)) return () => undefined;
    const handler = (event: MessageEvent) => {
        const msg = event.data;
        if (!msg || msg.type !== 'FCM_NAVIGATE' || typeof msg.url !== 'string') return;
        try {
            const target = new URL(msg.url, window.location.origin);
            if (target.origin === window.location.origin) window.location.assign(target.href);
        } catch {
            /* abaikan URL tidak valid */
        }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
};
